import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export const PROJECT_STATUS = {
  active: { label: "Active", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  on_hold: { label: "On hold", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  completed: { label: "Completed", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

const EMPTY = { name: "", description: "", status: "active", template: "none", opening_date: "", kind: "project" };

const Templates = ({ value, onChange }) => {
  const [list, setList] = useState([]);
  useEffect(() => {
    api.get("/project-templates").then((r) => setList(r.data));
  }, []);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid="project-template-select"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">None, normal project</SelectItem>
        {list.map((t) => <SelectItem key={t.key} value={t.key}>{t.label} · {t.count} items</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

export const ProjectDialog = ({ open, project, onClose, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setF(project ? { name: project.name, description: project.description || "", status: project.status, template: project.template || "none", opening_date: project.opening_date || "", kind: project.kind || "project" } : EMPTY);
  }, [open, project]);

  const save = async () => {
    setBusy(true);
    try {
      if (project) await api.put(`/projects/${project.id}`, f);
      else await api.post("/projects", f);
      toast.success(`${f.kind === "location" ? "Location" : "Project"} ${project ? "saved" : "created"}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="project-dialog">
        <DialogHeader><DialogTitle className="text-2xl text-[#24474A]">{project ? `Edit ${f.kind}` : "New project / location"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2" data-testid="project-kind-toggle">
            {[["project", "Project", "Has an end, e.g. a new store"], ["location", "Location only", "Ongoing, e.g. Head office"]].map(([k, l, d]) => (
              <button key={k} type="button" onClick={() => setF({ ...f, kind: k })} data-testid={`project-kind-${k}`}
                className={`text-left rounded-xl border p-3 transition-colors ${f.kind === k ? "border-[#24474A] bg-[#EAF2F2]" : "border-slate-200 hover:border-[#24474A]/40"}`}>
                <div className="text-sm font-bold text-slate-800">{l}</div><div className="text-xs text-slate-500">{d}</div>
              </button>
            ))}
          </div>
          <div className="space-y-1.5"><Label>{f.kind === "location" ? "Location name" : "Project name"}</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="project-name-input" /></div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="project-description-input" /></div>
          {f.kind === "project" && <>
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="project-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(PROJECT_STATUS).filter(([k]) => k !== "completed" || project?.status === "completed").map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
            {project && project.status !== "completed" && <p className="text-xs text-slate-400">To finish this project, use Complete project on its page.</p>}
          </div>
          <div className="space-y-1.5">
            <Label>Pre-defined tasks</Label>
            <Templates value={f.template} onChange={(v) => setF({ ...f, template: v })} />
            <p className="text-xs text-slate-400">Adds a ready-made checklist to this project. Switching back to None hides it but keeps its progress.</p>
          </div>
          {f.template !== "none" && (
            <div className="space-y-1.5">
              <Label>Planned opening day (optional)</Label>
              <Input type="date" value={f.opening_date} onChange={(e) => setF({ ...f, opening_date: e.target.value })} data-testid="project-opening-date-input" />
              <p className="text-xs text-slate-400">Item due dates are counted back from this day.</p>
            </div>
          )}
          </>}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" className="rounded-full" onClick={onClose}>Cancel</Button>
          <Button className="rounded-full" onClick={save} disabled={busy || !f.name.trim()} data-testid="project-save-btn">{project ? "Save" : f.kind === "location" ? "Create location" : "Create project"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
